import React, { useEffect, useRef, useState } from "react";

interface VideoProps {
    url: string;
    mobileVideoUrl?: string;
}

const MOBILE_QUERY = "(max-width: 768px)";

export const Video: React.FC<VideoProps> = ({ url, mobileVideoUrl }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const progressRef = useRef<SVGCircleElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(56);
    const [isMobileViewport, setIsMobileViewport] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mql = window.matchMedia(MOBILE_QUERY);
        const onChange = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
        setIsMobileViewport(mql.matches);
        if (mql.addEventListener) {
            mql.addEventListener("change", onChange);
            return () => mql.removeEventListener("change", onChange);
        } else {
            mql.addListener(onChange);
            return () => mql.removeListener(onChange);
        }
    }, []);

    const videoSrc = isMobileViewport && mobileVideoUrl ? mobileVideoUrl : url;

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        video.currentTime = 0;
        video.load();
        video.muted = true;
        video
            .play()
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false));
    }, [videoSrc]);

    useEffect(() => {
        const video = videoRef.current;
        const progressCircle = progressRef.current;
        if (!video) return;

        const onPause = () => console.log("Video paused");
        const onError = (e: Event) => console.error("Video error:", e);
        const updateProgress = () => {
            if (!video || !progressCircle || !video.duration) return;
            const rawProgress = (video.currentTime / video.duration) * 100;
            const converted = 56 * (1 - rawProgress / 100);
            setProgress(converted);

            progressCircle.style.strokeDashoffset = `${converted}`;
            progressCircle.style.transition = converted === 56 ? "" : "stroke-dashoffset .3s";
            progressCircle.style.stroke = video.paused ? "transparent" : "#cdcbcb";
        };

        video.addEventListener("pause", onPause);
        video.addEventListener("error", onError);
        video.addEventListener("timeupdate", updateProgress);

        return () => {
            video.removeEventListener("pause", onPause);
            video.removeEventListener("error", onError);
            video.removeEventListener("timeupdate", updateProgress);
        };
    }, []);

    const togglePlayback = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video.play();
            setIsPlaying(true);
        } else {
            video.pause();
            setIsPlaying(false);
        }
    };

    return (
        <div className={`Hero__video ${isMobileViewport ? "Hero__video--mobile" : ""}`}>
            <div className="Hero__videoInner" data-target="video-control">
                <video ref={videoRef} muted autoPlay loop playsInline className="video-play-control">
                    <source src={videoSrc} type="video/mp4" data-object-fit="cover" data-object-position="center" />
                    Your browser doesn't support HTML5 video.
                </video>

                <div className="Hero__video-controls">
                    <div className="Hero__video-controls__wrapper wrapper">
                        <div className="Hero__video-play">
                            <button
                                type="button"
                                className="Hero__video-play-button"
                                aria-label="play/pause"
                                onClick={togglePlayback}
                            >
                                <div className="Hero__video-play-button__svg-progress">
                                    <svg height="100%" viewBox="0 0 20 20" width="100%" style={{ overflow: "visible" }}>
                                        <circle
                                            ref={progressRef}
                                            cx="50%"
                                            cy="50%"
                                            fill="none"
                                            strokeWidth="0.5"
                                            r="9"
                                            strokeLinecap="round"
                                            id="progress-bar"
                                            strokeDasharray="56"
                                            strokeDashoffset={progress}
                                        />
                                    </svg>
                                </div>
                                {isPlaying ? (
                                    <span className="Hero__video-pause-icon">
                                        <img src="/Static/Images/Icons/pause-icon.svg" alt="Pause" />
                                    </span>
                                ) : (
                                    <span className="Hero__video-play-icon">
                                        <img src="/Static/Images/Icons/play-icon.svg" alt="Play" />
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
